import React from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export type PopupType = 'privacy' | 'terms' | null;
interface PrivacyPopupProps {
  openPopup: PopupType;
  onClose: () => void;
}
export function PrivacyPopup({
  openPopup,
  onClose
}: PrivacyPopupProps) {
  const isOpen = openPopup !== null;
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      body: <div className="space-y-4 text-sm text-gray-700 leading-relaxed" data-id="element-150">
          <p data-id="element-151">
            Respetamos tu privacidad y nos comprometemos a protegerla. Esta
            política explica cómo recopilamos, usamos y protegemos tu
            información personal.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-152">
            1. Recopilación de Información
          </h5>
          <p data-id="element-153">
            Solo recopilamos la información necesaria para procesar tu pedido y
            entregar el producto digital (nombre y correo electrónico). No
            almacenamos información de tarjetas de crédito; los pagos son
            procesados por proveedores seguros externos.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-154">2. Uso de la Información</h5>
          <p data-id="element-155">
            Tu correo electrónico se utiliza únicamente para enviarte el
            producto adquirido y actualizaciones relevantes. No vendemos ni
            compartimos tus datos con terceros.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-156">3. Seguridad</h5>
          <p data-id="element-157">
            Implementamos medidas de seguridad estándar de la industria para
            proteger tus datos contra acceso no autorizado.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-158">4. Cookies</h5>
          <p data-id="element-159">
            Este sitio puede utilizar cookies técnicas necesarias para su
            funcionamiento. No se utilizan cookies de seguimiento de terceros
            sin tu consentimiento.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-160">5. Tus Derechos</h5>
          <p data-id="element-161">
            Tienes derecho a acceder, rectificar o eliminar tus datos personales
            en cualquier momento. Para ejercer estos derechos, contáctanos por
            correo electrónico.
          </p>
        </div>
    },
    terms: {
      title: 'Términos y Condiciones',
      body: <div className="space-y-4 text-sm text-gray-700 leading-relaxed" data-id="element-162">
          <p data-id="element-163">
            Al adquirir "El Interruptor Digital", aceptas los siguientes
            términos y condiciones de uso.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-164">1. Licencia de Uso</h5>
          <p data-id="element-165">
            Obtienes una licencia personal, no exclusiva e intransferible para
            uso doméstico. Queda prohibida la distribución, reventa o copia
            pública de este material sin autorización expresa.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-166">2. Propiedad Intelectual</h5>
          <p data-id="element-167">
            Todo el contenido de esta guía es propiedad intelectual de sus
            autores. Está protegido por las leyes de derechos de autor
            aplicables.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-168">3. Política de Reembolso</h5>
          <p data-id="element-169">
            Ofrecemos reembolso dentro del período indicado al momento de la
            compra si la guía no cumple tus expectativas. Para solicitarlo,
            contáctanos con tu comprobante de pago.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-170">
            4. Limitación de Responsabilidad
          </h5>
          <p data-id="element-171">
            Esta guía es un recurso educativo de apoyo. Los resultados pueden
            variar según cada familia y situación particular. No sustituye
            asesoramiento profesional especializado.
          </p>
          <h5 className="font-bold text-[#1a1a2e]" data-id="element-172">5. Modificaciones</h5>
          <p data-id="element-173">
            Nos reservamos el derecho de actualizar estos términos en cualquier
            momento. Las actualizaciones serán comunicadas a través de los
            canales oficiales.
          </p>
        </div>
    }
  };
  const current = openPopup ? content[openPopup] : null;
  return <AnimatePresence data-id="element-174">
      {isOpen && current && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" onClick={onClose} data-id="element-175" />
          <motion.div initial={{
        opacity: 0,
        y: 60,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 60,
        scale: 0.96
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 m-auto z-50 w-full max-w-xl h-fit max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{
        top: '50%',
        transform: 'translateY(-50%)',
        position: 'fixed',
        left: 0,
        right: 0
      }} data-id="element-176">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 flex-shrink-0" data-id="element-177">
              <h3 className="font-bold text-lg text-[#1a1a2e] font-montserrat" data-id="element-178">
                {current.title}
              </h3>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors" aria-label="Cerrar" data-id="element-179">
                <X className="w-5 h-5" data-id="element-180" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-181">{current.body}</div>

            <div className="p-4 border-t bg-gray-50 text-right flex-shrink-0" data-id="element-182">
              <button onClick={onClose} className="px-6 py-2 bg-[#1a1a2e] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium" data-id="element-183">
                Entendido
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}